import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Paper, MenuItem, Select, FormControl, InputLabel, Chip, Autocomplete, Alert, Switch, FormControlLabel } from '@mui/material';

const countries = [
  'Argentina', 'Bolivia', 'Brasil', 'Chile', 'Colombia', 'Costa Rica',
  'Cuba', 'Ecuador', 'El Salvador', 'España', 'Guatemala', 'Honduras',
  'México', 'Nicaragua', 'Panamá', 'Paraguay', 'Perú', 'Puerto Rico',
  'República Dominicana', 'Uruguay', 'Venezuela'
];
const commonVegetables = [
  'Apio', 'Berenjena', 'Brócoli', 'Calabacín', 'Calabaza', 'Cebolla',
  'Col', 'Coliflor', 'Espárragos', 'Espinaca', 'Judías verdes', 'Lechuga',
  'Pepino', 'Pimiento', 'Remolacha', 'Repollo', 'Tomate', 'Zanahoria',
  'Acelgas', 'Alcachofa', 'Apio nabo', 'Berro', 'Bok choy', 'Coles de Bruselas',
  'Endivias', 'Hinojo', 'Kale', 'Nabo', 'Puerro', 'Rábano', 'Rúcula'
];

export default function Profile() {
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  })();
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    country: user?.country || '',
    weekly_goal: user?.weekly_goal || 30,
    excluded_vegetables: user?.excluded_vegetables ? JSON.parse(user.excluded_vegetables) : [],
    ia_consent: !!user?.ia_consent
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSuccess(false);
    setError(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSuccess(false);
    setError(null);
    try {
      const res = await fetch('/api/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: user.id,
          fullName: form.full_name,
          country: form.country,
          weeklyGoal: form.weekly_goal,
          excludedVegetables: form.excluded_vegetables,
          iaConsent: form.ia_consent
        })
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        localStorage.setItem('user', JSON.stringify({ ...user, ...form, excluded_vegetables: JSON.stringify(form.excluded_vegetables) }));
        setSuccess(true);
      } else {
        setError(data.error || 'Error al guardar los cambios');
      }
      setIsSaving(false);
    } catch (err) {
      setError('Error al guardar los cambios');
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#f4faef',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2
    }}>
      <Paper elevation={4} sx={{
        p: 4,
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: 420,
        width: '100%',
        bgcolor: 'white',
        mb: 4
      }}>
        <Typography variant="h5" fontWeight={700} color="primary" align="center" mb={2}>
          Mi perfil
        </Typography>
        <TextField
          fullWidth
          label="Nombre completo"
          value={form.full_name}
          onChange={e => handleChange('full_name', e.target.value)}
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>País o región</InputLabel>
          <Select
            value={form.country}
            label="País o región"
            onChange={e => handleChange('country', e.target.value)}
          >
            {countries.map((country) => (
              <MenuItem key={country} value={country}>{country}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          type="number"
          label="Objetivo semanal de verduras"
          value={form.weekly_goal}
          onChange={e => handleChange('weekly_goal', Number(e.target.value))}
          inputProps={{ min: 20, max: 50 }}
          sx={{ mb: 2 }}
        />
        <Autocomplete
          multiple
          options={commonVegetables}
          value={form.excluded_vegetables}
          onChange={(_, newValue) => handleChange('excluded_vegetables', newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                sx={{ bgcolor: '#ffebee', borderColor: '#e57373' }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Verduras a excluir"
              placeholder="Escribe para buscar..."
            />
          )}
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.ia_consent}
              onChange={e => handleChange('ia_consent', e.target.checked)}
              color="primary"
            />
          }
          label="Quiero usar el análisis con IA"
          sx={{ mb: 2 }}
        />
        {success && <Alert severity="success" sx={{ mb: 2 }}>¡Cambios guardados!</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          sx={{ borderRadius: 3, fontWeight: 700, fontSize: '1.1rem', py: 1.5, mb: 2 }}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          fullWidth
          sx={{ borderRadius: 3, fontWeight: 700, fontSize: '1.1rem', py: 1.5 }}
          onClick={() => {
            localStorage.removeItem('user');
            window.location.reload();
          }}
        >
          Cerrar sesión
        </Button>
      </Paper>
    </Box>
  );
} 